import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Issuer details printed on every invoice (from the TaleCrafter invoice template).
const ISSUER = {
    name: "STE TaleCrafter",
    addressLine1: "Imm. Tamayaoz A1-1",
    addressLine2: "Centre Urbain Nord, Tunis",
    taxId: "1886193F/A/M/000",
    phone: "+216 96 811 445",
    email: "contact@talecrafter.com",
    // Leave empty to print a dotted line instead.
    bank: "",
    rib: "",
    signatoryTitle: "La Gérante",
    signatoryName: "Maroua NEFZI",
};

// Template colors (RGB)
const PURPLE = [75, 0, 130];
const CYAN = [0, 194, 224];
const ORANGE = [247, 137, 74];
const LAVENDER = [243, 240, 248];
const LAVENDER_DARK = [200, 190, 220];
const BORDER = [216, 210, 230];
const DARK = [33, 33, 33];
const GRAY = [90, 90, 90];
const LIGHT_GRAY = [140, 140, 140];

const PAGE_W = 210;
const MARGIN = 15;
const RIGHT = PAGE_W - MARGIN;
const CONTENT_BOTTOM = 268; // the footer starts below this line

let logoCache = null;

const LOGO_URL = `${process.env.PUBLIC_URL || ""}/assets/logo/tale.png`;


const loadLogo = () => {
    if (logoCache) return logoCache;
    logoCache = new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => {
            logoCache = null;
            resolve(null);
        };
        img.src = LOGO_URL;
    });
    return logoCache;
};

/**
 * Generate PDF invoice from invoice data
 * @param {Object} invoice - Invoice data from API
 * @returns {Promise<jsPDF>} PDF document
 */
export const generateInvoicePDF = async (invoice) => {
    const logo = await loadLogo();
    const doc = new jsPDF({ unit: "mm", format: "a4" });

    doc.setProperties({
        title: `Facture ${invoice.id}`,
        subject: "Facture TaleCrafter",
        author: ISSUER.name,
        keywords: "facture, invoice, tale",
        creator: "Tale Platform",
    });

    // ---------- Totals ----------
    const items = invoice.items || [];
    const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.unitPrice) || 0) * (parseInt(item.quantity) || 0), 0);
    const totalDiscount = items.reduce((sum, item) => sum + (parseFloat(item.discountValue) || 0), 0);
    const afterDiscount = subtotal - totalDiscount;
    const taxAmount = parseFloat(invoice.tax) || 0;
    const taxRate = afterDiscount > 0 ? Math.round((taxAmount / afterDiscount) * 100) : 19;
    const fiscalStamp = parseFloat(invoice.fiscalStamp ?? invoice.timbreFiscal) || 0;
    const total = afterDiscount + taxAmount + fiscalStamp;

    // ---------- Header ----------
    doc.setFillColor(...CYAN);
    doc.rect(0, 0, PAGE_W, 3, "F");

    if (logo) {
        const h = 15;
        const w = Math.min((logo.naturalWidth / logo.naturalHeight) * h, 55);
        doc.addImage(logo, "PNG", MARGIN, 11, w, h);
    } else {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(26);
        doc.setTextColor(...PURPLE);
        doc.text("tale", MARGIN, 23);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.setTextColor(...DARK);
    doc.text("Facture", RIGHT, 25, { align: "right" });
    doc.setDrawColor(...ORANGE);
    doc.setLineWidth(1);
    doc.line(RIGHT - 25, 28.5, RIGHT, 28.5);

    // Issuer
    doc.setFontSize(11);
    doc.setTextColor(...PURPLE);
    doc.text(ISSUER.name, MARGIN, 44);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...GRAY);
    doc.text(`${ISSUER.addressLine1}, ${ISSUER.addressLine2}`, MARGIN, 50);
    doc.text(`M/F : ${ISSUER.taxId}`, MARGIN, 55);
    doc.text(`Tél : ${ISSUER.phone}  ·  ${ISSUER.email}`, MARGIN, 60);

    // Invoice number and dates
    field(doc, "N° facture :", invoice.id, 122, 44, RIGHT);
    field(doc, "Date :", formatDate(invoice.creationDate), 122, 50, RIGHT);
    field(doc, "Échéance :", formatDate(invoice.dueDate), 122, 56, RIGHT);

    // ---------- Billed to ----------
    const client = invoice.company || {};
    const user = invoice.user || {};
    const clientName = client.companyName || `${user.firstname || ""} ${user.lastname || ""}`.trim();

    doc.setFillColor(...LAVENDER);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.roundedRect(MARGIN, 68, RIGHT - MARGIN, 32, 3, 3, "FD");
    doc.setFillColor(...CYAN);
    doc.rect(MARGIN, 71, 1.3, 26, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...PURPLE);
    doc.text("Facturé à", 21, 76.5);

    field(doc, "Client / Raison sociale :", clientName, 21, 84, 104);
    field(doc, "Adresse :", client.address || user.address, 21, 90, 104);
    field(doc, "Réf. bon de commande :", invoice.purchaseOrderRef, 21, 96, 104);
    field(doc, "Matricule fiscal :", client.taxId || user.taxId, 110, 84, 189);
    field(doc, "Tél. :", client.phoneNumber || user.phoneNumber, 110, 90, 189);
    field(doc, "E-mail :", client.email || user.email, 110, 96, 189);

    field(doc, "Objet :", invoice.subject || invoice.object, MARGIN, 109, RIGHT, { size: 10, color: PURPLE });

    // ---------- Items ----------
    const rows = items.map((item, index) => {
        const unitPrice = parseFloat(item.unitPrice) || 0;
        const quantity = parseInt(item.quantity) || 0;
        const discountPercentage = parseFloat(item.discountPercentage) || 0;
        const discountValue = parseFloat(item.discountValue) || 0;
        const discount = discountPercentage > 0 ? `${discountPercentage} %` : discountValue > 0 ? formatAmount(discountValue) : "-";

        return [
            String(index + 1),
            item.name || "Article sans nom",
            String(quantity),
            formatAmount(unitPrice),
            discount,
            formatAmount(unitPrice * quantity - discountValue),
        ];
    });


    autoTable(doc, {
        startY: 115,
        head: [["N°", "Désignation", "Qté", "P.U. HT", "Remise", "Total HT"]],
        body: rows,
        theme: "grid",
        margin: { left: MARGIN, right: MARGIN, top: 20, bottom: 297 - CONTENT_BOTTOM },
        styles: {
            font: "helvetica",
            fontSize: 9,
            textColor: DARK,
            lineColor: BORDER,
            lineWidth: 0.2,
            minCellHeight: 6.5,
            valign: "middle",
        },
        headStyles: { fillColor: PURPLE, textColor: 255, fontStyle: "bold", halign: "center" },
        alternateRowStyles: { fillColor: LAVENDER },
        columnStyles: {
            0: { cellWidth: 12, halign: "center" },
            1: { cellWidth: "auto" },
            2: { cellWidth: 16, halign: "center" },
            3: { cellWidth: 28, halign: "right" },
            4: { cellWidth: 20, halign: "center" },
            5: { cellWidth: 30, halign: "right" },
        },
    });

    // ---------- Totals and payment terms ----------
    let y = doc.lastAutoTable.finalY + 8;
    // totals (45) + amount in words (16) + signature (20)
    if (y + 82 > CONTENT_BOTTOM) {
        doc.addPage();
        y = 20;
    }

    const totalsRows = [
        ["Total HT", subtotal],
        ["Remise", totalDiscount],
        ["Total net HT", afterDiscount],
        [`TVA ( ${taxRate} % )`, taxAmount],
        ["Timbre fiscal", fiscalStamp],
        ["Total TTC", total],
    ].map(([label, amount]) => [label, `${formatAmount(amount)} TND`]);

    autoTable(doc, {
        startY: y,
        body: totalsRows,
        theme: "grid",
        margin: { left: 125, right: MARGIN },
        styles: { font: "helvetica", fontSize: 9, textColor: DARK, lineColor: BORDER, lineWidth: 0.2, minCellHeight: 7.5, valign: "middle" },
        columnStyles: { 0: { cellWidth: 36 }, 1: { halign: "right" } },
        didParseCell: (data) => {
            if (data.row.index === totalsRows.length - 1) {
                data.cell.styles.fillColor = PURPLE;
                data.cell.styles.textColor = 255;
                data.cell.styles.fontStyle = "bold";
            }
        },
    });
    const totalsEnd = doc.lastAutoTable.finalY;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...PURPLE);
    doc.text("Conditions de règlement", MARGIN, y + 5);

    field(doc, "Mode de paiement :", invoice.paymentMethod, MARGIN, y + 13, 112);
    field(doc, "Banque :", ISSUER.bank, MARGIN, y + 19, 112);
    field(doc, "RIB :", ISSUER.rib, MARGIN, y + 25, 112);

    let notesEnd = y + 33;
    if (invoice.notes) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(...DARK);
        doc.text("Notes :", MARGIN, notesEnd);
        doc.setFont("helvetica", "normal");
        const lines = doc.splitTextToSize(invoice.notes, 112 - MARGIN - 14).slice(0, 4);
        doc.text(lines, MARGIN + 14, notesEnd);
        notesEnd += lines.length * 4.5;
    } else {
        field(doc, "Notes :", "", MARGIN, notesEnd, 112);
        dotted(doc, MARGIN, notesEnd + 6, 112);
        notesEnd += 6;
    }

    // Amount in words
    let wy = Math.max(totalsEnd, notesEnd) + 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...DARK);
    doc.text("Arrêtée la présente facture à la somme de :", MARGIN, wy);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...GRAY);
    const words = doc.splitTextToSize(amountInWords(total), RIGHT - MARGIN);
    doc.text(words, MARGIN, wy + 6);
    wy += 6 + words.length * 5;

    // Signature
    const sx = 165;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...DARK);
    doc.text(ISSUER.signatoryTitle, sx, wy + 6, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(ISSUER.signatoryName, sx, wy + 12, { align: "center" });
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...LIGHT_GRAY);
    doc.text("Cachet et signature", sx, wy + 17, { align: "center" });

    // ---------- Footer on every page ----------
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        drawFooter(doc);
    }

    return doc;
};

const drawFooter = (doc) => {
    doc.setFillColor(...CYAN);
    doc.rect(0, 273, PAGE_W, 1.2, "F");
    doc.setFillColor(...LAVENDER);
    doc.rect(0, 274.2, PAGE_W, 18.8, "F");
    doc.setFillColor(...LAVENDER_DARK);
    doc.rect(0, 293, PAGE_W, 4, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...PURPLE);
    doc.text(ISSUER.name, MARGIN, 279.5);
    doc.text("CONTACT", 120, 279.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...DARK);
    doc.text(ISSUER.addressLine1, MARGIN, 283.5);
    doc.text(ISSUER.addressLine2, MARGIN, 287.5);
    doc.text(`M/F : ${ISSUER.taxId}`, MARGIN, 291.5);
    doc.text(`Tél : ${ISSUER.phone}`, 120, 283.5);
    doc.text(ISSUER.email, 120, 287.5);
};

/**
 * Bold label followed by its value, or by a dotted line when the value is empty.
 */
const field = (doc, label, value, x, y, xEnd, { size = 9, color = DARK } = {}) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(size);
    doc.setTextColor(...color);
    doc.text(label, x, y);

    const vx = x + doc.getTextWidth(label) + 2;
    const text = value === undefined || value === null ? "" : String(value).trim();
    if (text) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...DARK);
        doc.text(doc.splitTextToSize(text, xEnd - vx)[0], vx, y);
    } else {
        dotted(doc, vx, y + 0.8, xEnd);
    }
};

const dotted = (doc, x1, y, x2) => {
    doc.setDrawColor(...LAVENDER_DARK);
    doc.setLineWidth(0.3);
    doc.setLineDashPattern([0.4, 0.8], 0);
    doc.line(x1, y, x2, y);
    doc.setLineDashPattern([], 0);
};

const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

/**
 * 1234.5 -> "1 234,500". Built by hand: Intl uses narrow no-break spaces that jsPDF fonts cannot draw.
 */
const formatAmount = (amount) => {
    const millimes = Math.round(Math.abs(amount || 0) * 1000);
    const dinars = Math.floor(millimes / 1000).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    const decimals = String(millimes % 1000).padStart(3, "0");
    return `${amount < 0 ? "-" : ""}${dinars},${decimals}`;
};

// ---------- Amount in French words ----------
const UNITS = ["zéro", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf", "dix",
    "onze", "douze", "treize", "quatorze", "quinze", "seize"];
const TENS = ["", "dix", "vingt", "trente", "quarante", "cinquante", "soixante"];

const below100 = (n) => {
    if (n < 17) return UNITS[n];
    if (n < 20) return `dix-${UNITS[n - 10]}`;
    const t = Math.floor(n / 10);
    const u = n % 10;
    if (t === 7) return n === 71 ? "soixante et onze" : `soixante-${below100(n - 60)}`;
    if (t === 8) return u === 0 ? "quatre-vingts" : `quatre-vingt-${UNITS[u]}`;
    if (t === 9) return `quatre-vingt-${below100(n - 80)}`;
    if (u === 0) return TENS[t];
    return u === 1 ? `${TENS[t]} et un` : `${TENS[t]}-${UNITS[u]}`;
};

const below1000 = (n) => {
    const h = Math.floor(n / 100);
    const r = n % 100;
    if (h === 0) return below100(r);
    const hundreds = h === 1 ? "cent" : `${UNITS[h]} cent`;
    if (r === 0) return h > 1 ? `${hundreds}s` : hundreds;
    return `${hundreds} ${below100(r)}`;
};

const numberToWords = (n) => {
    if (n === 0) return "zéro";
    const parts = [];
    const millions = Math.floor(n / 1000000);
    const thousands = Math.floor((n % 1000000) / 1000);
    const rest = n % 1000;
    if (millions) parts.push(`${below1000(millions)} million${millions > 1 ? "s" : ""}`);
    // "mille" is invariable and "cents"/"vingts" lose their s before it.
    if (thousands) parts.push(thousands === 1 ? "mille" : `${below1000(thousands).replace(/(cent|vingt)s$/, "$1")} mille`);
    if (rest) parts.push(below1000(rest));
    return parts.join(" ");
};

const amountInWords = (amount) => {
    const totalMillimes = Math.round(Math.abs(amount || 0) * 1000);
    const dinars = Math.floor(totalMillimes / 1000);
    const millimes = totalMillimes % 1000;
    let text = `${numberToWords(dinars)} dinar${dinars > 1 ? "s" : ""}`;
    if (millimes) text += ` et ${numberToWords(millimes)} millime${millimes > 1 ? "s" : ""}`;
    return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Download PDF invoice
 * @param {Object} invoice - Invoice data
 * @param {string} fileName - Optional custom file name
 */
export const downloadInvoicePDF = async (invoice, fileName = null) => {
    try {
        const doc = await generateInvoicePDF(invoice);
        const name = fileName || `Facture-${invoice.id}-${new Date().toISOString().split("T")[0]}.pdf`;
        doc.save(name);
    } catch (error) {
        console.error("Failed to generate PDF:", error);
        throw error;
    }
};

/**
 * Open PDF in new window
 * @param {Object} invoice - Invoice data
 */
export const openInvoicePDF = async (invoice) => {
    try {
        const doc = await generateInvoicePDF(invoice);
        const pdfUrl = URL.createObjectURL(doc.output("blob"));
        window.open(pdfUrl, "_blank");
        // Revoke later: revoking right away can break the new tab before it loads.
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (error) {
        console.error("Failed to generate PDF:", error);
        throw error;
    }
};

/**
 * Get PDF as Blob (for email attachments)
 * @param {Object} invoice - Invoice data
 * @returns {Promise<Blob>} PDF blob
 */
export const getInvoicePDFBlob = async (invoice) => {
    try {
        const doc = await generateInvoicePDF(invoice);
        return doc.output("blob");
    } catch (error) {
        console.error("Failed to generate PDF:", error);
        throw error;
    }
};